import React, { useCallback, useRef, useState } from 'react';
import { useRequest } from 'ahooks';

import { verifyProviderByLast4 } from '../../../../utils/serviceCalls/provider';
import { isValidLast4Format } from '../../../../utils/validation';
import {
  CheckIcon,
  LoadingIndicator,
} from '../../../Shared/SearchCombobox/Icons';
import { VALIDATION_MESSAGES } from '../../constants';
import { FIELDS, INPUT_WITH_ICON_PADDING_CLASSES } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormContextSafe, useFormField } from './hooks';

/**
 * Hook to verify Last 4 digits against the API as you type.
 *
 * Key behaviors:
 * - Debounces API calls by 300ms
 * - Shows "busy" state during debounce AND loading (via dirty tracking)
 * - Sets verifiedLast4 form field on success (for form submission gating)
 * - Uses ref for API errors to avoid stale closure in validate()
 *
 * @param {string|null} pwid - The provider's PWID for verification
 * @param {string} last4 - Last 4 digits to verify
 * @param {boolean} disabled - Whether verification should be skipped
 */
function useLast4Verification(pwid, last4, disabled) {
  const { setValue, trigger } = useFormContextSafe();

  const apiErrorRef = useRef(/** @type {string|null} */ (null));

  // Track last verified value to compute "dirty" state
  const [lastVerifiedLast4, setLastVerifiedLast4] = useState('');

  // Dirty = input changed since last verification completed
  const dirty = last4 !== lastVerifiedLast4;

  const shouldVerify =
    !disabled && !!pwid && !!last4 && isValidLast4Format(last4);

  /** Sets the verified value in form state (gates form submission) */
  const setVerifiedLast4 = useCallback(
    /** @param {string} value */
    (value) => {
      setValue(FIELDS.verifiedLast4, value, { shouldValidate: false });
    },
    [setValue],
  );

  /** Clears API error immediately - call on input change */
  const clearApiError = useCallback(() => {
    apiErrorRef.current = null;
  }, []);

  /** Clears verification state - call on input change */
  const clearVerification = useCallback(() => {
    setVerifiedLast4('');
  }, [setVerifiedLast4]);

  const { loading } = useRequest(
    async () => {
      if (!pwid || !last4) return;

      const result = await verifyProviderByLast4(pwid, last4);

      if (result.success) {
        apiErrorRef.current = null;
        setVerifiedLast4(last4);
      } else {
        apiErrorRef.current = VALIDATION_MESSAGES.LAST4_MISMATCH;
        setVerifiedLast4('');
      }
    },
    {
      refreshDeps: [pwid, last4],
      debounceWait: 300,
      ready: shouldVerify,
      onBefore: () => {
        // Clear previous state when new verification starts
        apiErrorRef.current = null;
        setVerifiedLast4('');
      },
      onError: () => {
        apiErrorRef.current = VALIDATION_MESSAGES.LAST4_VERIFICATION_FAILED;
        setVerifiedLast4('');
      },
      onFinally: () => {
        setLastVerifiedLast4(last4 ?? '');
        trigger(FIELDS.last4);
      },
    },
  );

  // Busy = should verify AND (input changed OR request in flight)
  const busy = shouldVerify && (dirty || loading);

  return {
    busy,
    apiErrorRef,
    clearApiError,
    clearVerification,
  };
}

/**
 * Last 4 input field with verification-as-you-type functionality.
 *
 * Features:
 * - Validates format (4 alphanumeric characters)
 * - Verifies against API with 300ms debounce
 * - Shows loading indicator during debounce and API call
 * - Shows green checkmark when verified
 * - Sets verifiedLast4 form field to gate form submission
 * - Required field (unlike NPI which is optional)
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string|null} props.pwid - The provider's PWID for verification
 */
export default function Last4Field({ disabled, pwid }) {
  const {
    register,
    validateField,
    errorMessage,
    handleKeyDown,
    currentValue,
    getValues,
    watch,
  } = useFormField(FIELDS.last4, disabled);

  const { busy, apiErrorRef, clearApiError, clearVerification } =
    useLast4Verification(pwid, currentValue || '', disabled);

  // Watch verifiedLast4 for UI display (checkmark)
  const verifiedLast4Value = (watch(FIELDS.verifiedLast4) || '').trim();

  // Compute verification status for UI
  // Only show checkmark when: not disabled, not busy, and verified value matches input
  const isVerified =
    !disabled &&
    !busy &&
    !!verifiedLast4Value &&
    verifiedLast4Value === (currentValue || '').trim();

  const { onChange: rhfOnChange, ...restRegistration } = register(
    FIELDS.last4,
    {
      validate: (val = '') => {
        if (disabled) return true;

        // Required field
        if (!val) return VALIDATION_MESSAGES.LAST4_REQUIRED;

        // Format validation
        if (!isValidLast4Format(val)) return VALIDATION_MESSAGES.LAST4_INVALID;

        // API error (from ref to avoid stale closure)
        if (apiErrorRef.current) return apiErrorRef.current;

        // Verification gate: form invalid until API confirms
        // Read directly from form state, not closure (TR-3)
        const currentVerifiedLast4 = (
          getValues(FIELDS.verifiedLast4) || ''
        ).trim();
        const isCurrentlyVerified = currentVerifiedLast4 === val.trim();

        // Return empty string = invalid but no visible error
        // This keeps form.isValid false while verification is pending
        if (!isCurrentlyVerified) return '';

        return true;
      },
    },
  );

  const handleChange = (e) => {
    // Clear stale state IMMEDIATELY on any input change
    // (onBefore only fires after debounce wait)
    clearApiError();
    clearVerification();

    rhfOnChange(e);
    validateField();
  };

  return (
    <FieldWrapper label="Last 4 of DEA or License" errorMessage={errorMessage}>
      <div className="relative">
        <input
          {...restRegistration}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type="text"
          maxLength={4}
          placeholder={
            pwid ? 'Enter last 4 characters' : 'Select a provider first'
          }
          disabled={disabled || !pwid}
          className={INPUT_WITH_ICON_PADDING_CLASSES}
          aria-invalid={!!errorMessage}
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {busy ? (
            <LoadingIndicator />
          ) : isVerified ? (
            <CheckIcon className="h-5 w-5 text-green-500" />
          ) : null}
        </div>
      </div>
    </FieldWrapper>
  );
}
